import React, { useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { Alert, Box, IconButton, TextField, Typography } from '@mui/material'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ForwardRoundedIcon from '@mui/icons-material/ForwardRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import SeeMoreButton from 'src/feed/FeedComponents/SeeMoreButton'
import ScrollableFeedContainer from 'src/feed/ScrollableFeedContainer/ScrollableFeedContainer'
import { Failure } from '../../api/failureReason'
import FailureMessage from '../../components/FailureMessage'
import { ConversationPaging } from '../../api/patientConversation'
import { chatDayLabel } from '../../format'
import { advancedColors, surfaces } from '../../theme'
import { useIsPanelPreview } from '../panelPreviewContext'
import ChatMessageBubble from './ChatMessageBubble'
import DayDivider from './DayDivider'
import { chatComposerInputSx } from './chatComposerStyles'
import { useChatInputHeight } from './ChatComposerLayout'
import {
  AttachmentButton,
  AttachmentPreview,
  PendingAttachment,
  useAttachmentUpload,
} from './attachments/AttachmentPicker'
import { ChatBubbleEntry, ForwardedFeedItem, SendMessageError, forwardedItemPreviewLabel } from './types'

const GROUP_WINDOW_MINUTES = 5

const continuesPrevious = (entry: ChatBubbleEntry, previous: ChatBubbleEntry | undefined): boolean =>
  !!previous &&
  entry.variant !== 'activity' &&
  previous.variant !== 'activity' &&
  entry.channel === previous.channel &&
  entry.isMine === previous.isMine &&
  entry.authorCoachId === previous.authorCoachId &&
  DateTime.fromISO(entry.createdAt).diff(DateTime.fromISO(previous.createdAt), 'minutes').minutes < GROUP_WINDOW_MINUTES

// `boundaryKey` is `undefined` until the marker loads, which holds the scroll position back.
type Unread = { firstId: string | null; boundaryKey: number | null | undefined }

type Props = {
  uid: string
  careTeam?: boolean
  entries: ChatBubbleEntry[]
  // Blanks the empty state, so it doesn't flash before the messages arrive.
  loading?: boolean
  attachmentsEnabled?: boolean
  onSend?: (
    text: string,
    attachments: PendingAttachment[],
    forwardedItem: ForwardedFeedItem | null
  ) => void | Promise<unknown>
  sending?: boolean
  error?: Failure | null
  emptyLabel: string
  placeholder?: string
  // Replaces the built-in composer, which then handles its own attachments.
  composer?: React.ReactNode
  paging?: Pick<ConversationPaging, 'hasOlder' | 'isFetchingOlder' | 'fetchMoreOlder'>
  unread?: Unread
  pendingForward?: ForwardedFeedItem | null
  onClearPendingForward?: () => void
}

const ChatThreadShell = ({
  uid,
  careTeam = false,
  entries,
  loading = false,
  attachmentsEnabled = true,
  onSend,
  sending,
  error,
  emptyLabel,
  placeholder,
  composer,
  paging,
  unread,
  pendingForward = null,
  onClearPendingForward,
}: Props) => {
  const preview = useIsPanelPreview()
  const accent = careTeam ? surfaces.blue.fg : advancedColors.green
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const inputHeight = useChatInputHeight('careTeam', inputRef)
  const firstUnreadRef = useRef<HTMLDivElement>(null)
  const attachments = useAttachmentUpload(uid, preview || sending)

  // Mid-upload files would miss this message and land in the next draft.
  const busy = preview || sending || attachments.uploading
  const hasContent = !!draft.trim() || !!pendingForward || attachments.pending.length > 0
  const canSend = !busy && hasContent

  const submit = async () => {
    if (!canSend || !onSend) {
      return
    }
    try {
      await onSend(draft.trim(), attachments.pending, pendingForward)
      setDraft('')
      onClearPendingForward?.()
      attachments.clear()
    } catch (failure) {
      // Rejected files are already deleted server-side, so never resend them.
      const rejected = (failure as SendMessageError).rejectedFileIDs ?? []
      rejected.forEach((fileID) => attachments.remove(fileID))
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {entries.length === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', px: 1.5, py: 1 }}>
          {!loading && (
            <Box
              sx={{
                m: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                color: advancedColors.inkFaint,
              }}
            >
              <ChatBubbleOutlineRoundedIcon />
              <Typography variant="body2" sx={{ color: advancedColors.inkMuted, textAlign: 'center' }}>
                {emptyLabel}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Keyed per patient like Standard's feed.
        <ScrollableFeedContainer
          key={uid}
          items={entries.map((entry) => ({ itemID: entry.id }))}
          firstUnreadRef={firstUnreadRef}
          unreadBoundaryKey={unread ? unread.boundaryKey : null}
        >
          {paging?.hasOlder && (
            <SeeMoreButton fetchMoreOlder={paging.fetchMoreOlder} isFetchingOlder={paging.isFetchingOlder} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {entries.map((entry, index) => {
              const previous = entries[index - 1]
              const startsDay = !previous || chatDayLabel(entry.createdAt) !== chatDayLabel(previous.createdAt)
              const isFirstUnread = entry.id === unread?.firstId
              const showDivider = startsDay || isFirstUnread
              const continued = !showDivider && continuesPrevious(entry, previous)
              const gapAbove = continued ? 0.5 : 1.5
              return (
                <React.Fragment key={entry.id}>
                  {isFirstUnread && <div ref={firstUnreadRef} />}
                  {isFirstUnread && (
                    <DayDivider
                      isNew
                      flush
                      isOnTop={paging?.hasOlder && index === 0}
                      label={startsDay ? chatDayLabel(entry.createdAt) : undefined}
                    />
                  )}
                  {startsDay && !isFirstUnread && (
                    <Box sx={{ my: 1.5 }}>
                      <DayDivider label={chatDayLabel(entry.createdAt)} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: entry.variant !== 'activity' && entry.isMine ? 'flex-end' : 'flex-start',
                      mt: showDivider ? 0 : gapAbove,
                    }}
                  >
                    <ChatMessageBubble entry={entry} continued={continued} careTeam={careTeam} />
                  </Box>
                </React.Fragment>
              )
            })}
          </Box>
        </ScrollableFeedContainer>
      )}

      {error && (
        <Alert severity="error" sx={{ mx: 1 }}>
          <FailureMessage failure={error} />
        </Alert>
      )}

      {composer ??
        (onSend && (
          <>
            {pendingForward && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1.5,
                  pt: 1,
                  borderTop: `1px solid ${advancedColors.grayLightLabel}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <ForwardRoundedIcon fontSize="small" sx={{ color: advancedColors.inkFaint, flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ color: advancedColors.inkMuted }}>
                    Forwarding: {forwardedItemPreviewLabel(pendingForward)}
                  </Typography>
                </Box>
                <IconButton size="small" aria-label="Cancel forwarding" onClick={onClearPendingForward}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <AttachmentPreview
              pending={attachments.pending}
              onRemove={attachments.remove}
              uploading={attachments.uploading}
              error={attachments.error}
            />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
                borderTop: pendingForward ? 'none' : `1px solid ${advancedColors.line}`,
                px: 1.5,
                py: 1.5,
                flexShrink: 0,
                backgroundColor: careTeam ? surfaces.blue.fg : advancedColors.surface,
                '& > button': { mb: '4px' },
              }}
            >
              {attachmentsEnabled && (
                <AttachmentButton
                  onDark={careTeam}
                  disabled={preview || sending}
                  onSelect={(files) => {
                    attachments.addFiles(files).catch(() => undefined)
                  }}
                />
              )}
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={6}
                inputRef={inputRef}
                inputProps={{ 'aria-label': careTeam ? 'Message the care team' : 'Message' }}
                placeholder={placeholder ?? 'Write a message…'}
                disabled={preview}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault()
                    submit().catch(() => undefined)
                  }
                }}
                sx={chatComposerInputSx(accent, inputHeight)}
              />
              <IconButton
                aria-label="Send message"
                disabled={!canSend}
                onClick={() => {
                  submit().catch(() => undefined)
                }}
                sx={{
                  flexShrink: 0,
                  backgroundColor: careTeam ? advancedColors.surface : accent,
                  color: careTeam ? surfaces.blue.fg : advancedColors.surface,
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: careTeam ? surfaces.blue.bg : accent, filter: 'brightness(0.9)' },
                  '&.Mui-disabled': {
                    backgroundColor: careTeam ? surfaces.blue.bg : advancedColors.grayLightLabel,
                    color: advancedColors.inkFaint,
                  },
                }}
              >
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        ))}
    </Box>
  )
}

export default ChatThreadShell
